import AppPaths from '../../../../AppPaths';
import {
  createZendeskCancellationTicket,
  createZendeskReplacementTicket,
  createZendeskTroubleshootingTicket,
} from '../../../../graphql-operations';
import { getLoadingOrErrorElement } from '../../../../lib/util';
import * as gqlTypes from '../../../../types/gql-op-types';
import { useMutation } from '@apollo/client';
import { RequestReason } from '../RequestSent/RequestSent';
import { useHistory } from 'react-router-dom';
import { SubscriptionCategory } from '../util/types';
import i18next from '../../../../../i18n';

export interface useZendeskCancellationTicketProps {
  analyticsEvent: (input: any) => void;
  selectedCancelReason: gqlTypes.zendeskCancellationReason;
  device: gqlTypes.purchaseSubscriptionDeviceDetails;
  subscriptionCategory: SubscriptionCategory;
  otherText?: string;
  redirectAfterSuccess?: boolean;
}

export function useZendeskCancellationTicket({
  analyticsEvent,
  selectedCancelReason,
  device,
  subscriptionCategory,
  otherText,
  redirectAfterSuccess = true,
}: useZendeskCancellationTicketProps) {
  const history = useHistory();
  const [mutation, { loading, error }] = useMutation<
    gqlTypes.ECOMMERCE_createZendeskCancellationTicket,
    gqlTypes.ECOMMERCE_createZendeskCancellationTicketVariables
  >(createZendeskCancellationTicket, {
    onCompleted: (data) => {
      if (data.createZendeskCancellationTicket?.ticketCreated) {
        analyticsEvent({
          petId: device.pet?.id,
          cancellationReason: selectedCancelReason,
          otherReasonText: otherText ?? undefined,
          subscriptionCategory,
          moduleId: device.moduleId,
        });
        if (redirectAfterSuccess) {
          history.push({
            pathname: AppPaths.Subscription.RequestSent(device.moduleId),
            search: window.location.search,
            state: {
              requestType: RequestReason.Cancellation,
              cancellationReason: selectedCancelReason,
              subscriptionCategory,
            },
          });
        }
      } else {
        throw new Error(
          i18next.t('unable_to_send_request_please_try_again_later_if_problem_persists_reach_out_to_support'),
        );
      }
    },
  });

  const loadingOrErrorElement = getLoadingOrErrorElement(loading, error);

  return { mutation, loading: loadingOrErrorElement };
}

export interface useZendeskReplacementTicketProps {
  analyticsEvent: (input: any) => void;
  device: gqlTypes.purchaseSubscriptionDeviceDetails;
  replacementReason: string;
  subscriptionCategory: SubscriptionCategory;
}

export function useZendeskReplacementTicket({
  analyticsEvent,
  device,
  replacementReason,
  subscriptionCategory,
}: useZendeskReplacementTicketProps) {
  const history = useHistory();
  const [mutation, { loading, error }] = useMutation<
    gqlTypes.ECOMMERCE_createZendeskReplacementTicket,
    gqlTypes.ECOMMERCE_createZendeskReplacementTicketVariables
  >(createZendeskReplacementTicket, {
    onCompleted: (data) => {
      if (data.createZendeskReplacementTicket?.ticketCreated) {
        analyticsEvent({ petId: device.pet?.id, replacementReason, subscriptionCategory });
        history.push({
          pathname: AppPaths.Subscription.RequestSent(device.moduleId),
          search: window.location.search,
          state: {
            requestType: RequestReason.Replacement,
            replacementReason,
            subscriptionCategory,
          },
        });
      } else {
        throw new Error(
          i18next.t('unable_to_send_request_please_try_again_later_if_problem_persists_reach_out_to_support'),
        );
      }
    },
  });

  const loadingOrErrorElement = getLoadingOrErrorElement(loading, error);

  return { mutation, loading: loadingOrErrorElement };
}

export interface useZendeskTroubleshootingTicketProps {
  analyticsEvent: (input: any) => void;
  device: gqlTypes.purchaseSubscriptionDeviceDetails;
  selectedTroubleshootingReason: gqlTypes.zendeskCancellationReason;
  otherText?: string;
}

export function useZendeskTroubleshootingTicket({
  analyticsEvent,
  device,
  selectedTroubleshootingReason,
  otherText,
}: useZendeskTroubleshootingTicketProps) {
  const history = useHistory();
  const [mutation, { loading, error }] = useMutation<
    gqlTypes.ECOMMERCE_createZendeskTroubleshootingTicket,
    gqlTypes.ECOMMERCE_createZendeskTroubleshootingTicketVariables
  >(createZendeskTroubleshootingTicket, {
    onCompleted: (data) => {
      if (data.createZendeskTroubleshootingTicket?.ticketCreated) {
        analyticsEvent({ petId: device.pet?.id, troubleshootingReason: selectedTroubleshootingReason, otherText });
        history.push({
          pathname: AppPaths.Subscription.RequestSent(device.moduleId),
          search: window.location.search,
          state: {
            requestType: RequestReason.Troubleshooting,
            troubleshootingReason: selectedTroubleshootingReason,
          },
        });
      } else {
        throw new Error(
          i18next.t('unable_to_send_request_please_try_again_later_if_problem_persists_reach_out_to_support'),
        );
      }
    },
  });

  const loadingOrErrorElement = getLoadingOrErrorElement(loading, error);
  return { mutation, loading: loadingOrErrorElement };
}
